import {
  Button,
  Center,
  ComboboxItem,
  Divider,
  Group,
  Loader,
  Stack,
  Table,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { getCustomerOrderHistory } from "api/report.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import { navigateTo } from "common/navigation";
import DebtorSelect from "pages/admin/components/DebtorSelection";
import YearSelect from "pages/admin/components/YearSelection";
import React, { useState } from "react";
import { RptCustomerOrderHistories } from "types/model.types";

const AdminCustomerOrderHistoryReportPage: React.FC = () => {
  const [debtorId, setDebtorId] = useState<number | null>(null);
  const [data, setData] = useState<RptCustomerOrderHistories>([]);
  const [loading, setLoading] = useState(false);
  const apiFetchRef = React.useRef<boolean>(false);

  const [selectedYear, setSelectedYear] = useState<string>(
    new Date().getFullYear().toString(),
  );

  /**
   *
   * @returns an array of customer order history data
   */
  const fetchData = async () => {
    if (!debtorId) {
      notifications.show({
        title: `Debtor Selection`,
        message: "Please choose an account to report on",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Success,
      });

      return;
    }

    if (apiFetchRef.current === true) return;
    apiFetchRef.current = true;

    setLoading(true);

    const result = await safeApiCall(
      getCustomerOrderHistory(
        debtorId,
        Number(selectedYear ?? new Date().getFullYear()),
      ),
    );
    setData(result?.data ?? []);
    apiFetchRef.current = false;
    setLoading(false);
  };

  const handleYearChange = (value: string | null, _option: ComboboxItem) => {
    if (value) setSelectedYear(value);
  };

  return (
    <Stack>
      <Group justify="space-between" align="end" gap="sm">
        <Title order={2}>Customer Order History Report</Title>
        <Button
          disabled={!((debtorId ?? 0) > 0)}
          onClick={() => {
            navigateTo(
              `/print/cust-order-history?id=${debtorId}&y=${selectedYear}`,
            );
          }}
          variant="gradient"
        >
          Print
        </Button>
      </Group>

      <Group align="end" gap="sm">
        <DebtorSelect
          name="Debtor"
          ariaLabel="Debtor Selection"
          value={null}
          label="Select Account"
          placeholder="Type debtor name or id"
          onChange={(debtorId: number | null, _: string) => {
            setDebtorId(debtorId);
          }}
        />
        <YearSelect value={selectedYear} onChange={handleYearChange} />
        <Button onClick={fetchData}>Refresh</Button>
      </Group>
      <Divider label="Define filters above..."></Divider>
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Debtor #</Table.Th>
            <Table.Th>Product Code</Table.Th>
            <Table.Th>Month</Table.Th>
            <Table.Th>Year</Table.Th>
            <Table.Th>Total</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading ? (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Center>
                  <Loader />
                </Center>
              </Table.Td>
            </Table.Tr>
          ) : (
            <>
              {data.map((row, index) => (
                <Table.Tr key={`${index}-${row.id}`}>
                  <Table.Td>{row.id}</Table.Td>
                  <Table.Td>{row.productId}</Table.Td>
                  <Table.Td>{row.monthName}</Table.Td>
                  <Table.Td>{row.yearOrdered}</Table.Td>
                  <Table.Td>{row.total}</Table.Td>
                </Table.Tr>
              ))}
            </>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

export default AdminCustomerOrderHistoryReportPage;
